import {cssVars, lazyCustomElements, listen} from 'nuffjs';

const conf = {
	canvasAttr: 'animationCanvas',
	defaultColor: '#483AC2',
	mainAccentColorProp: 'mainAccentColor',
};

const observerCallbacks = [];
const observer = new IntersectionObserver(
	(entries) => {
		for (const callback of observerCallbacks) {
			callback(entries);
		}
	},
	{
		root: document,
		rootMargin: '0px 0px 0px 0px',
		threshold: [0.2],
	}
);

let SimplexNoise = null;
let PIXI = null;
let SmoothGraphics = null;

async function preloadLibraries() {
	if (!PIXI) {
		// load in parallel
		const libs = await Promise.all([
			import('../../external/simplex-noise'),
			import('../../external/pixi'),
			import('../../external/smooth-graphics'),
		]);
		SimplexNoise = libs[0].SimplexNoise;
		PIXI = libs[1].PIXI;
		SmoothGraphics = libs[2].SmoothGraphics;
	}
}

class ContourLines extends HTMLElement {
	static conf = conf;

	params = {
		height: 0.75,
		amplitude: 185,
		speedX: 0.15,
		speedY: 0.15,
		speedZ: 0.3,
		frequenceX: 1.6,
		frequenceY: 1.5,
		frequenceZ: 4,
	};

	isOutOfScreen = false;
	isPaused = true;
	intersecting = false;
	allowedToPlay = false;
	nx = 0;
	ny = 0;
	nz = 0;
	cols = 60;

	firstConnectedCallback() {
		// //for testing animation parameters - please don't delete
		// //GUI
		// const gui = new dat.GUI({autoPlace: false});
		// const g = document.createElement('div');
		// g.setAttribute('id', 'dja-gui-container');
		// document.body.appendChild(g);
		// g.appendChild(gui.domElement);
		// const f1 = gui.addFolder('Noise Waves');
		// f1.add(this.params, 'height', 0.1, 2).step(0.01);
		// f1.add(this.params, 'amplitude', 0, 400).step(1);
		// f1.add(this.params, 'speedX', -10, 10).step(0.01);
		// f1.add(this.params, 'speedY', -10, 10).step(0.01);
		// f1.add(this.params, 'speedZ', -10, 10).step(0.01);
		// f1.add(this.params, 'frequenceX', 1, 10);
		// f1.add(this.params, 'frequenceY', 1, 10);
		// f1.add(this.params, 'frequenceZ', 1, 10);
		// f1.open();
		// gui.closed = true;

		this.color = cssVars(document.body).get(conf.mainAccentColorProp, conf.defaultColor);
		observerCallbacks.push(this.onIntersect.bind(this));
		observer.observe(this);

		preloadLibraries().then(async () => {
			this.initPixi();
			this.initAnimation();
			listen(window, 'resize', this.onResize.bind(this), {throttle: true});

			const animationsToggler = await lazyCustomElements.querySelector(
				document.body,
				'[is="app-animation-toggler"]'
			);
			if (animationsToggler) {
				listen(document.body, animationsToggler.getEventName(), this.onAnimationsToggle.bind(this));
				this.allowedToPlay = !animationsToggler.isPaused();
				this.updatePlayback();
				if (!this.allowedToPlay) {
					this.draw();
				}
			}
		});
	}

	initAnimation() {
		this.simplex = new SimplexNoise();
		this.onResize();
	}

	initPixi() {
		this.renderer = PIXI.autoDetectRenderer({width: 1, height: 1, backgroundColor: 0xffffff});
		this.appendChild(this.renderer.view);
		this.stage = new PIXI.Container();
		this.graphics = new SmoothGraphics();
		this.stage.addChild(this.graphics);
	}

	updatePlayback() {
		if (this.isPaused && this.allowedToPlay && this.intersecting) {
			this.isPaused = false;
			requestAnimationFrame(this.onFrame.bind(this));
		} else {
			this.isPaused = true;
		}
	}

	onIntersect(entries) {
		for (const entry of entries) {
			if (entry.target === this) {
				this.intersecting = !!entry.isIntersecting;
				this.updatePlayback();
				break;
			}
		}
	}

	onAnimationsToggle(event) {
		this.allowedToPlay = !event.detail.isPaused;
		this.updatePlayback();
	}

	onFrame() {
		if (this.isPaused || !this.allowedToPlay || !this.intersecting) {
			return;
		}
		this.draw();
		requestAnimationFrame(this.onFrame.bind(this));
	}

	createPrimitives() {
		this.graphics.clear();
		this.primitives = [];
		for (let y = 0; y < this.rows; y++) {
			this.primitives[y] = [];
			for (let x = 0; x < this.cols; x++) {
				this.primitives[y][x] = {x: 0, y: 0}; //.add(new Point(0, 0));
			}
		}
	}

	draw() {
		this.nx += this.params.speedX / 1000;
		this.ny += this.params.speedY / 1000;
		this.nz += this.params.speedZ / 100;

		this.graphics.clear();
		this.graphics.lineStyle(1, parseInt(this.color.substring(this.color.indexOf('#') + 1), 16));

		let x = 0;
		let y = 0;
		let p;
		let ypsilon;

		for (y = 0; y < this.rows; y++) {
			p = this.primitives[y];
			this.graphics.moveTo(p[0].x, p[0].y);
			for (x = 0; x < this.cols; x++) {
				ypsilon =
					this.h * (1 - this.params.height) * 0.25 +
					((this.w * this.params.height * 1.1) / this.rows) * (y - 20) +
					this.simplex.noise3D(
						this.nx + x * this.params.frequenceX * 0.01,
						this.ny + y * this.params.frequenceY * 0.01,
						this.nz * this.params.frequenceZ * 0.01
					) *
						this.params.amplitude;

				p[x].y = (this.h / this.cols) * x;

				//if(p[x].x === undefined) {
				//console.log('undefined');
				p[x].x = 1.5 * ypsilon;
				//} else {
				//p[x].x += 1.5 * (ypsilon - p[x].x);
				//}

				this.graphics.lineTo(p[x].x, p[x].y);
			}
		}
		this.graphics.endFill();
		this.renderer.render(this.stage);
	}

	onResize(e) {
		this.h = this.clientHeight;
		this.w = window.innerWidth;
		this.rows = Math.floor(this.w * 0.14);
		this.createPrimitives();
		this.graphics.clear();
		this.renderer.resize(window.innerWidth, this.h);

		this.draw();
	}
}

export {ContourLines};
